import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { Rocket, Star, TrendingUp } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import Button from './Button';

const FIELD_ICON_VARIANTS = [Star, Rocket, TrendingUp];
const STAR_SIZE = 11;

function seededRandom(seed) {
  let value = seed % 2147483647;
  if (value <= 0) value += 2147483646;
  return () => {
    value = (value * 16807) % 2147483647;
    return (value - 1) / 2147483646;
  };
}

/**
 * Icon field. Stronger on the right, fades toward the left.
 */
function buildStarField(seedKey, count = 28) {
  let seed = 0;
  for (let i = 0; i < seedKey.length; i += 1) {
    seed = (seed * 31 + seedKey.charCodeAt(i)) >>> 0;
  }
  const rand = seededRandom(seed || 42);
  const cols = 7;
  const rows = Math.ceil(count / cols);
  const iconVariant = FIELD_ICON_VARIANTS[Math.floor(rand() * FIELD_ICON_VARIANTS.length)];

  return Array.from({ length: count }, (_, index) => {
    const col = index % cols;
    const row = Math.floor(index / cols);
    const cellW = 100 / cols;
    const cellH = 100 / rows;
    const x = (col + 0.2 + rand() * 0.6) * cellW;
    const y = (row + 0.2 + rand() * 0.6) * cellH;
    const t = x / 100; // 0 = left, 1 = right
    const opacity = 0.12 + t * 0.4; // left faint → right strong

    return {
      id: index,
      x,
      y,
      opacity,
      rotate: -20 + rand() * 40,
      Icon: iconVariant,
    };
  });
}

function MotivationalStarField({ seed }) {
  const stars = useMemo(() => buildStarField(seed), [seed]);

  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]"
      style={{
        WebkitMaskImage: 'linear-gradient(to right, transparent 8%, #000 55%, #000 100%)',
        maskImage: 'linear-gradient(to right, transparent 8%, #000 55%, #000 100%)',
      }}
      aria-hidden="true"
    >
      {stars.map((star) => {
        const Icon = star.Icon;
        return (
          <div
            key={star.id}
            className="absolute text-white"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: STAR_SIZE,
              height: STAR_SIZE,
              opacity: star.opacity,
              marginLeft: -STAR_SIZE / 2,
              marginTop: -STAR_SIZE / 2,
              transform: `rotate(${star.rotate}deg)`,
            }}
          >
            <Icon className="h-full w-full" strokeWidth={1.55} fill="currentColor" fillOpacity={0.28} />
          </div>
        );
      })}
    </div>
  );
}

function NextBestAction({
  action,
  onAction,
  username,
  children,
  className = '',
}) {
  const { t, formatMessage } = useLanguage();
  const [fieldSeed] = useState(
    () => `${action?.id || 'nba'}-${username || 'guest'}-${Date.now()}-${Math.floor(Math.random() * 1e6)}`
  );

  if (!action) return null;

  const greetingTemplate =
    t.enrola?.motivationalBanner?.greeting || t.ux?.nextBestAction?.greeting || 'Hey, {name}';
  const greeting = formatMessage(greetingTemplate, {
    name: username || t.common?.user || 'there',
  });
  const headline = action.motivation || action.title;

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
      className={`card-premium card-premium-accent relative overflow-hidden rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 p-5 text-white shadow-glow sm:rounded-3xl sm:p-6 ${className}`}
      aria-label={action.title || headline}
    >
      <MotivationalStarField seed={fieldSeed} />
      <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/10 blur-2xl" />

      <div className="relative z-[1] flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-white/85">{greeting}</p>
          <h2 className="mt-1.5 max-w-2xl text-base font-semibold italic leading-relaxed sm:text-lg">
            {headline}
          </h2>
          {action.meta ? (
            <p className="mt-1.5 text-xs font-semibold uppercase tracking-wide text-white/75">
              {action.meta}
            </p>
          ) : null}
          {children ? <div className="mt-3">{children}</div> : null}
        </div>
        <Button
          type="button"
          variant="secondary"
          size="default"
          icon={ArrowRightIcon}
          iconPosition="right"
          onClick={() => onAction?.(action)}
          className="btn-crystal-shine w-full shrink-0 border-white/50 bg-white/90 text-orange-700 hover:bg-white sm:w-auto"
        >
          {action.cta}
        </Button>
      </div>
    </motion.section>
  );
}

export default NextBestAction;
